import { POSTS, formatPostDate, postPath, readingMinutes, type BlogPost } from '../lib/blog';

/**
 * /blog/ — list of every post, newest first.
 *
 * The newest post is shown as a wide "featured" card; the rest fall into a
 * grid underneath. Each post's 1200×630 social image doubles as its thumbnail,
 * so there is one image to produce per post, not two.
 */

function PostMeta({ post }: { post: BlogPost }) {
  return (
    <p className="m-0 text-sm text-landing-deep-muted">
      <span className="font-semibold uppercase tracking-[0.08em] text-landing-accent">
        {post.category}
      </span>
      {' · '}
      <time dateTime={post.publishedAt}>{formatPostDate(post.publishedAt)}</time>
      {' · '}
      {readingMinutes(post)} min read
    </p>
  );
}

function FeaturedCard({ post }: { post: BlogPost }) {
  return (
    <a
      href={postPath(post)}
      className="group grid overflow-hidden rounded-3xl border border-landing-card-line bg-white no-underline shadow-sm transition-shadow hover:shadow-xl lg:grid-cols-[1.15fr_1fr]"
    >
      <img
        src={post.ogImage}
        alt=""
        width={1200}
        height={630}
        className="aspect-[1200/630] h-full w-full object-cover"
      />
      <div className="flex flex-col justify-center gap-4 p-6 lg:p-10">
        <PostMeta post={post} />
        <h2 className="m-0 text-2xl font-bold leading-[1.2] tracking-[-0.5px] text-landing-slate group-hover:underline lg:text-[32px]">
          {post.title}
        </h2>
        <p className="m-0 text-base leading-[1.65] text-landing-outline">{post.description}</p>
        <p className="m-0 mt-2 text-base font-semibold text-landing-accent">
          Read the article <span aria-hidden="true">→</span>
        </p>
      </div>
    </a>
  );
}

function PostCard({ post }: { post: BlogPost }) {
  return (
    <a
      href={postPath(post)}
      className="group flex flex-col overflow-hidden rounded-2xl border border-landing-card-line bg-white no-underline shadow-sm transition-shadow hover:shadow-lg"
    >
      <img
        src={post.ogImage}
        alt=""
        width={1200}
        height={630}
        loading="lazy"
        className="aspect-[1200/630] w-full object-cover"
      />
      <div className="flex flex-1 flex-col gap-3 p-6">
        <PostMeta post={post} />
        <h2 className="m-0 text-xl font-bold leading-[1.3] text-landing-slate group-hover:underline">
          {post.title}
        </h2>
        <p className="m-0 text-[15px] leading-[1.6] text-landing-outline">{post.description}</p>
      </div>
    </a>
  );
}

export function BlogIndex() {
  const [featured, ...rest] = POSTS;

  return (
    <main className="w-full bg-white">
      <header className="w-full bg-gradient-to-b from-landing-peach to-white px-5 pb-10 pt-12 sm:px-6 md:px-8 lg:px-10 lg:pb-14 lg:pt-20 xl:px-16">
        <div className="mx-auto max-w-[1200px]">
          <p className="mb-4 mt-0 text-sm font-semibold uppercase tracking-[0.12em] text-landing-accent">
            WisdomX Orbis Blog
          </p>
          <h1 className="m-0 max-w-[860px] text-[clamp(32px,5vw,52px)] font-extrabold leading-[1.1] tracking-[-1px] text-landing-slate">
            Insights on legacy estates and decision intelligence
          </h1>
          <p className="mb-0 mt-5 max-w-[720px] text-lg leading-[1.6] text-landing-outline lg:text-xl">
            Thought leadership, research and practical guidance on retiring legacy systems safely —
            and turning hidden IT cost into decisions you can act on.
          </p>
        </div>
      </header>

      <div className="px-5 pb-16 sm:px-6 md:px-8 lg:px-10 lg:pb-24 xl:px-16">
        <div className="mx-auto flex max-w-[1200px] flex-col gap-10">
          {featured && <FeaturedCard post={featured} />}
          {rest.length > 0 && (
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {rest.map((post) => (
                <PostCard key={post.slug} post={post} />
              ))}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
